// STEP MOA-LIVE-VOICE-1B · 라모 AI 음성 엔진 (PTT + STT + 명령 디스패처)
// =============================================================================
// 목적
//   · 기존 라모 AI 캡슐 UI (상태 모델: off/idle/listening/processing/done/failed) 와
//     연결되는 음성 엔진 Hook.
//   · 1차 범위 (스펙 §3): 상품번호·다음/이전 상품·오버레이·가격·공지·타이머 제어.
//   · 가격 수정 · 주문 변경 · 방송 시작/종료는 명시적 미지원.
//
// 설계 원칙
//   · 기존 ops (useLiveOperations) 100% REUSE — 신규 WRITE 경로 금지.
//   · 호출어 감지 엔진은 **플러그러블 인터페이스**만 준비 (실제 wake-word SDK 는
//     STEP 1-C 에서 선택 후 attach).  이번 STEP 에서는 **PTT 백업만** 활성.
//   · 테스트 모드: 서버 STT (/voice-command) 는 호출 가능 (read-only · DB WRITE 0).
//     intent 디스패치는 isTestMode 분기로 로컬 ctx 만 변경.
//   · 오인식 반복 실행 방지: cooldown + 동일 intent 2초 중복 억제.
//   · LiveKit 송출 마이크 미접촉 — 전용 getUserMedia 스트림 (QuickAddModal 동일 패턴).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { WAKE_AWAIT_MS } from '@/lib/ramoWakeWordConfig';
import { isOverlayIntent, runOverlayIntent } from '@/lib/ramoOverlayVoice';

const COOLDOWN_MS = 1500;         // 1.5s · 디스패치 간 최소 간격
const DEDUPE_MS = 2000;           // 2s · 동일 intent 중복 억제
const MAX_RECORD_MS = 10 * 1000;  // 10s · PTT 안전 상한
const HISTORY_MAX = 10;

const _nowMs = () => Date.now();

/**
 * useRamoVoice({ scheduleId, ops, liveCtx, setLiveCtx, openNoticeEditor,
 *                isTestMode, enabled, onIntentDispatched })
 *
 * returns {
 *   enabled, status, lastResult, history,
 *   start, stop, cancel, enable, disable, toggle,
 *   attachWakeWord, detachWakeWord,
 * }
 */
const useRamoVoice = (opts = {}) => {
  const {
    scheduleId = null,
    ops = null,
    liveCtx = null,
    setLiveCtx = null,
    openNoticeEditor = null,
    clearLiveNotice = null,
    isTestMode = false,
    initialEnabled = false,
    onIntentDispatched = null,
    overlay = null,
  } = opts;

  const [enabled, setEnabled] = useState(!!initialEnabled);
  const [status, setStatus] = useState(initialEnabled ? 'idle' : 'off');
  const [lastResult, setLastResult] = useState('');
  const [history, setHistory] = useState([]);

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const lastDispatchRef = useRef({ ts: 0, intent: '' });
  const resetDoneTimerRef = useRef(null);
  const wakeWordRef = useRef(null);
  const enabledRef = useRef(!!initialEnabled);
  const statusRef = useRef(initialEnabled ? 'idle' : 'off');
  const wakeBusyRef = useRef(false);
  const sendAudioRef = useRef(null);
  enabledRef.current = enabled;
  statusRef.current = status;

  // ─ Cleanup helpers ─────────────────────────────────────────────────────
  const _stopTracks = useCallback(() => {
    try { streamRef.current?.getTracks?.().forEach((t) => { try { t.stop(); } catch {} }); } catch {}
    streamRef.current = null;
  }, []);
  const _clearRec = useCallback(() => {
    try { if (recRef.current && recRef.current.state === 'recording') recRef.current.stop(); } catch {}
    recRef.current = null;
    chunksRef.current = [];
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
  }, []);

  // ─ Status auto-reset after 'done'/'failed' ────────────────────────────
  const _scheduleIdleReset = useCallback((ms = 2500) => {
    if (resetDoneTimerRef.current) clearTimeout(resetDoneTimerRef.current);
    resetDoneTimerRef.current = setTimeout(() => {
      setStatus((s) => (s === 'done' || s === 'failed') ? 'idle' : s);
      setLastResult('');
    }, ms);
  }, []);

  // ─ Intent dispatcher — REUSE 기존 ops · 신규 WRITE 경로 X ───────────────
  const _dispatchIntent = useCallback(async (intent, params = {}) => {
    const now = _nowMs();
    // Cooldown · dedupe
    const since = now - lastDispatchRef.current.ts;
    if (since < COOLDOWN_MS) return { skipped: 'cooldown' };
    const key = `${intent}|${JSON.stringify(params || {})}`;
    if (key === lastDispatchRef.current.intent && since < DEDUPE_MS) return { skipped: 'dedupe' };
    lastDispatchRef.current = { ts: now, intent: key };

    const items = Array.isArray(liveCtx?.live_products) ? liveCtx.live_products : [];
    const activePid = liveCtx?.active_product?.product_id || null;
    const curPreset = liveCtx?.active_product_display?.preset || 'default';

    const _resultText = (txt) => {
      setLastResult(txt);
      setStatus('done');
      setHistory((h) => [{ ts: now, intent, params, text: txt, ok: true }, ...h].slice(0, HISTORY_MAX));
      if (typeof onIntentDispatched === 'function') { try { onIntentDispatched({ intent, params, ok: true }); } catch {} }
      _scheduleIdleReset(2500);
    };
    const _failText = (txt) => {
      setLastResult(txt);
      setStatus('failed');
      setHistory((h) => [{ ts: now, intent, params, text: txt, ok: false }, ...h].slice(0, HISTORY_MAX));
      if (typeof onIntentDispatched === 'function') { try { onIntentDispatched({ intent, params, ok: false }); } catch {} }
      _scheduleIdleReset(3000);
    };

    // ── Test-mode local dispatch (DB WRITE 0) ─────────────────────────
    const _testPatch = (fn) => {
      if (!setLiveCtx) return;
      setLiveCtx((prev) => fn(prev || { live_products: [] }));
    };

    // 테스트 모드 상품 표시 — 수동 소개 버튼과 동일 결과 (표시 + 소개 이력) · 이미 표시 중이면 이력 중복 기록 X.
    const _testIntroduce = (target) => {
      if (target.product_id === activePid && liveCtx?.active_product_display?.mode !== 'hidden') return;
      _testPatch((c) => ({
        ...c,
        active_product: target,
        active_product_display: { ...(c.active_product_display || {}), preset: curPreset === 'hidden' ? 'default' : (curPreset || 'default'), position: c.active_product_display?.position || 'bottom', mode: 'visible' },
        live_products: (c.live_products || []).map((p) => ({
          ...p, is_introduced: p.product_id === target.product_id ? true : p.is_introduced,
          introduced_at: p.product_id === target.product_id ? new Date().toISOString() : p.introduced_at,
        })),
      }));
    };
    const _show = async (target) => { if (isTestMode) _testIntroduce(target); else await ops.announce(target.product_id); };
    const _label = (t) => `${t.slot_number || (items.indexOf(t) + 1)}번 상품`;

    try {
      if (!scheduleId) { _failText('방송이 선택되지 않았어요.'); return; }

      // 상품 소개 (slot 번호)
      if (intent === 'product.introduce') {
        const slot = Number(params?.slot_number);
        const target = items.find((p) => p.slot_number === slot) || items[slot - 1];
        if (!target?.product_id) { _failText('해당 상품을 찾을 수 없습니다'); return; }
        await _show(target);
        _resultText(`${slot}번 상품 표시 완료`);
        return;
      }

      // 다음·이전 — 방송 상품 표시 순서 기준 · 순환 없음 · 경계에서는 무동작.
      if (intent === 'product.next' || intent === 'product.prev') {
        const delta = intent === 'product.next' ? +1 : -1;
        if (!items.length) { _failText('방송에 연결된 상품이 없어요'); return; }
        if (delta < 0 && params?.history) {
          // '아까 상품' — 현재 상품 직전에 소개된 이력이 있을 때만.
          const hist = items.filter((p) => p.introduced_at && p.product_id !== activePid)
            .sort((a, b) => Date.parse(b.introduced_at) - Date.parse(a.introduced_at));
          if (!hist.length) { _failText('어떤 상품인지 확실하지 않아 실행하지 않았어요'); return; }
          await _show(hist[0]);
          _resultText(`${_label(hist[0])} 다시 표시 완료`);
          return;
        }
        const idx = items.findIndex((p) => p.product_id === activePid);
        if (idx >= 0 && delta > 0 && idx >= items.length - 1) { _failText('마지막 상품입니다'); return; }
        if (idx >= 0 && delta < 0 && idx <= 0) { _failText('첫 번째 상품입니다'); return; }
        if (isTestMode) {
          const target = items[idx < 0 ? 0 : idx + delta];
          _testIntroduce(target);
        } else {
          await ops.gotoRelative(items, activePid, delta);
        }
        _resultText(delta > 0 ? '다음 상품 표시 완료' : '이전 상품 표시 완료');
        return;
      }

      if (intent === 'overlay.hide' || intent === 'overlay.price.hide') {
        if (!activePid) { _failText('표시 중인 상품이 없어요'); return; }
        if (isTestMode) {
          _testPatch((c) => ({
            ...c,
            active_product_display: { ...(c.active_product_display || {}), mode: 'hidden' },
          }));
        } else {
          await ops.hideOverlay(activePid);
        }
        _resultText('현재 상품 숨김 완료');
        return;
      }
      if (intent === 'overlay.show' || intent === 'overlay.price.show') {
        if (!activePid) { _failText('소개 중인 상품이 없어요.'); return; }
        const nextPreset = intent === 'overlay.price.show' ? 'price' : (curPreset === 'hidden' ? 'default' : curPreset || 'default');
        if (isTestMode) {
          _testPatch((c) => ({
            ...c,
            active_product_display: { ...(c.active_product_display || {}), preset: nextPreset, mode: 'visible' },
          }));
        } else {
          await ops.applyPreset(activePid, nextPreset);
        }
        _resultText(intent === 'overlay.price.show' ? '가격 오버레이를 띄웠어요.' : '오버레이를 다시 띄웠어요.');
        return;
      }

      if (isOverlayIntent(intent)) {
        const r = await runOverlayIntent(intent, params, {
          activePid, active: liveCtx?.active_product, display: liveCtx?.active_product_display || {},
          isTestMode, ops, testPatch: _testPatch, overlay,
        });
        if (r) { (r.ok ? _resultText : _failText)(r.text); return; }
      }

      if (intent === 'notice.show') {
        if (typeof openNoticeEditor === 'function') { try { openNoticeEditor(); } catch {} }
        _resultText('공지 편집기를 열었어요.');
        return;
      }
      if (intent === 'notice.hide') {
        if (isTestMode) {
          _testPatch((c) => ({ ...c, live_notice: null }));
        } else if (typeof clearLiveNotice === 'function') {
          await clearLiveNotice();
        } else {
          _failText('공지 삭제 핸들러가 없어요.');
          return;
        }
        _resultText('공지를 지웠어요.');
        return;
      }

      if (intent === 'timer.start') {
        const duration = Number(params?.duration_seconds) > 0 ? Number(params.duration_seconds) : 180;
        if (isTestMode) {
          _testPatch((c) => ({ ...c, timer: { state: 'running', duration_seconds: duration, end_at: new Date(Date.now() + duration * 1000).toISOString() } }));
        } else {
          await api.post(`/seller/broadcasts/${scheduleId}/timer`, { action: 'start', duration_seconds: duration, label: params?.label });
        }
        _resultText(`타이머 ${duration}초 시작했어요.`);
        return;
      }
      if (intent === 'timer.stop') {
        if (isTestMode) {
          _testPatch((c) => ({ ...c, timer: { ...(c.timer || {}), state: 'stopped' } }));
        } else {
          await api.post(`/seller/broadcasts/${scheduleId}/timer`, { action: 'stop' });
        }
        _resultText('타이머를 종료했어요.');
        return;
      }

      // wake / unknown
      if (intent === 'wake') {
        setStatus('detected');
        _scheduleIdleReset(1800);
        return;
      }
      _failText('지원하지 않는 명령이에요.');
    } catch (e) {
      _failText(e?.response?.data?.detail || '명령 실행에 실패했어요.');
    }
  }, [scheduleId, ops, liveCtx, setLiveCtx, openNoticeEditor, clearLiveNotice,
      isTestMode, onIntentDispatched, _scheduleIdleReset, overlay]);

  // ─ 공통 · 오디오 → Whisper/Claude → 디스패치 (PTT · 호출어 공용) ─
  const _sendAudio = useCallback(async (blob, filename, { fromWake = false } = {}) => {
    setStatus('processing');
    try {
      const fd = new FormData();
      fd.append('audio', blob, filename);
      const r = await api.post('/seller/live/voice-command', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 15000,
      });
      const intent = (r.data?.intent || 'unknown').trim();
      const params = r.data?.params || {};
      if (fromWake && intent === 'wake') {
        setLastResult('명령이 없어 호출 대기로 돌아갈게요.');
        setStatus('idle');
        _scheduleIdleReset(2000);
        return;
      }
      if (intent === 'unknown') {
        setLastResult(`"${(r.data?.transcript || '').slice(0, 30)}" — 명령을 이해하지 못했어요.`);
        setStatus('failed');
        setHistory((h) => [{ ts: _nowMs(), intent: 'unknown', text: r.data?.transcript || '', ok: false }, ...h].slice(0, HISTORY_MAX));
        _scheduleIdleReset(2500);
        return;
      }
      await _dispatchIntent(intent, params);
    } catch (e) {
      setStatus('failed');
      setLastResult(e?.response?.data?.detail || '음성 인식 실패');
      _scheduleIdleReset(2500);
    }
  }, [_dispatchIntent, _scheduleIdleReset]);
  sendAudioRef.current = _sendAudio;

  // ─ STEP WAKEWORD-FLEX-1 · 호출어 감지 → 명령 대기(awaiting) → 자동 녹음 → 기존 경로 ─
  const onWakeDetected = useCallback(async (provider, meta = {}) => {
    const _note = (r) => { try { provider?.setHandoff?.(r); } catch {} };
    if (!enabledRef.current) { _note('skip:voice_off'); return; }
    if (wakeBusyRef.current) { _note('skip:busy'); return; }
    if (['listening', 'processing', 'awaiting'].includes(statusRef.current)) { _note(`skip:status_${statusRef.current}`); return; }
    if (typeof provider?.captureCommand !== 'function') { _note('skip:no_capture'); return; }
    _note('awaiting');
    wakeBusyRef.current = true;
    if (resetDoneTimerRef.current) clearTimeout(resetDoneTimerRef.current);
    statusRef.current = 'awaiting';
    setStatus('awaiting');
    setLastResult('');
    try {
      const r = await provider.captureCommand({
        timeoutMs: WAKE_AWAIT_MS,
        hasCommandHint: !!meta?.command,
        onSpeech: () => { statusRef.current = 'listening'; setStatus('listening'); },
      });
      _note(`capture:${r?.reason}`);
      if (!enabledRef.current) return;
      if (!r?.blob) {
        setStatus('idle');
        setLastResult(r?.reason === 'timeout' ? '명령이 없어 호출 대기로 돌아갈게요.' : '');
        _scheduleIdleReset(2000);
        return;
      }
      await sendAudioRef.current?.(r.blob, 'cmd.wav', { fromWake: true });
    } finally {
      wakeBusyRef.current = false;
    }
  }, [_scheduleIdleReset]);

  // ─ PTT · 녹음 시작/정지 ────────────────────────────────────────────────
  const start = useCallback(async () => {
    if (!enabled) return;
    if (status === 'listening' || status === 'processing' || status === 'awaiting') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus'))
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported?.('audio/mp4') ? 'audio/mp4' : '');
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        _stopTracks();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (!blob || blob.size < 400) {
          setStatus('idle'); setLastResult('');
          return;
        }
        const ext = (rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
        await _sendAudio(blob, `cmd.${ext}`);
      };
      rec.start();
      setStatus('listening');
      stopTimerRef.current = setTimeout(() => {
        try { if (recRef.current?.state === 'recording') recRef.current.stop(); } catch {}
      }, MAX_RECORD_MS);
    } catch (e) {
      setStatus('failed');
      setLastResult(e?.name === 'NotAllowedError' ? '마이크 권한을 허용해주세요.' : '마이크를 사용할 수 없어요.');
      _scheduleIdleReset(3000);
    }
  }, [enabled, status, _stopTracks, _sendAudio, _scheduleIdleReset]);

  const stop = useCallback(() => {
    try { if (recRef.current?.state === 'recording') recRef.current.stop(); } catch {}
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
  }, []);

  const cancel = useCallback(() => {
    _clearRec();
    _stopTracks();
    setStatus(enabled ? 'idle' : 'off');
    setLastResult('');
  }, [_clearRec, _stopTracks, enabled]);

  // ─ Enable / Disable / Toggle ─────────────────────────────────────────-
  const enable = useCallback(() => {
    setEnabled(true);
    setStatus('idle');
  }, []);
  const disable = useCallback(() => {
    cancel();
    setEnabled(false);
    setStatus('off');
    if (wakeWordRef.current?.dispose) { try { wakeWordRef.current.dispose(); } catch {} }
  }, [cancel]);
  const toggle = useCallback(() => { enabled ? disable() : enable(); }, [enabled, disable, enable]);

  // ─ Pluggable wake-word interface (STEP 1-C 에서 attach) ────────────────
  // provider contract:
  //   { init?: (ctx) => Promise<void>, dispose?: () => void, name?: string }
  // provider 가 wake-word 감지 시 호출해야 하는 콜백은 ctx.onDetect() 로 노출.
  const attachWakeWord = useCallback(async (provider) => {
    wakeWordRef.current = provider || null;
    if (provider?.init) {
      try {
        await provider.init({
          onDetect: (meta) => { onWakeDetected(provider, meta); },
        });
      } catch (e) {
        setStatus('failed');
        setLastResult('호출어 엔진 초기화 실패');
      }
    }
  }, [onWakeDetected]);
  const detachWakeWord = useCallback(() => {
    if (wakeWordRef.current?.dispose) { try { wakeWordRef.current.dispose(); } catch {} }
    wakeWordRef.current = null;
  }, []);

  // ─ Unmount cleanup ─────────────────────────────────────────────────────
  useEffect(() => () => {
    _clearRec(); _stopTracks();
    if (resetDoneTimerRef.current) clearTimeout(resetDoneTimerRef.current);
    if (wakeWordRef.current?.dispose) { try { wakeWordRef.current.dispose(); } catch {} }
  }, [_clearRec, _stopTracks]);

  return useMemo(() => ({
    enabled, status, lastResult, history,
    start, stop, cancel,
    enable, disable, toggle,
    attachWakeWord, detachWakeWord, onWakeDetected,
    dispatchIntent: _dispatchIntent,
  }), [enabled, status, lastResult, history, start, stop, cancel, enable, disable, toggle, attachWakeWord, detachWakeWord, onWakeDetected, _dispatchIntent]);
};

export default useRamoVoice;
